package dev.kotlinfest._2026.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.ContentContainerStyle
import dev.kotlinfest._2026.LatinFontFamily
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SiteLinks
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.widgets.SiteButton
import dev.kotlinfest._2026.components.widgets.SiteButtonLanguage
import dev.kotlinfest._2026.components.widgets.SiteButtonSize
import dev.kotlinfest._2026.components.widgets.SiteButtonType
import dev.kotlinfest._2026.components.widgets.TextButton
import dev.kotlinfest._2026.desktopPageHorizontalPadding
import dev.kotlinfest._2026.fixedPageBackground
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px

val FooterStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().minHeight(1031.px)
            .padding(top = 64.px, bottom = 64.px, leftRight = SiteLayout.MobileMarginPx.px)
            .backgroundColor(SitePalette.brand.softPink)
            .fixedPageBackground()
    }
    Breakpoint.MD {
        Modifier.minHeight(652.px).padding(top = 80.px, bottom = 62.px)
            .desktopPageHorizontalPadding()
            .fixedPageBackground()
    }
}

val FooterContentStyle = CssStyle {
    base { Modifier.gap(48.px) }
}

val FooterCtaStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { size(1.fr) }.gap(16.px) }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } }.gap(20.px) }
}

val FooterSocialTitleStyle = CssStyle {
    base { Modifier.fontSize(16.px).fontWeight(700).lineHeight(1.6).letterSpacing(0.1.em) }
    Breakpoint.MD {
        Modifier.lineHeight(2.0).letterSpacing(0.08.em).styleModifier {
            property("font-size", "clamp(16px, 1.81vw, 26px)")
            property("white-space", "nowrap")
        }
    }
}

val DesktopFooterBodyStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().styleModifier { property("gap", "clamp(20px, 2.99vw, 43px)") }
    }
}

val DesktopFooterDetailsStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().styleModifier { property("gap", "clamp(20px, 2.22vw, 32px)") }
    }
}

val DesktopFooterLinksStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(3) { size(1.fr) } }.gap(20.px) }
    cssRule("a") { Modifier.styleModifier { property("font-size", "clamp(12px, 1.18vw, 17px)") } }
    cssRule("span") { Modifier.styleModifier { property("font-size", "clamp(12px, 1.18vw, 17px)") } }
}

val DesktopFooterLogoStyle = CssStyle {
    base { Modifier.width(240.px).height(198.px) }
}

val DesktopFooterDividerStyle = CssStyle {
    base {
        Modifier.width(1.px).borderLeft(1.px, LineStyle.Solid, SitePalette.brand.ink).styleModifier {
            property("height", "clamp(120px, 13.75vw, 198px)")
        }
    }
}

val MobileFooterLinksStyle = CssStyle {
    base { Modifier.fillMaxWidth().display(DisplayStyle.Grid).gridTemplateColumns { repeat(2) { size(1.fr) } }.gap(16.px) }
}

val FooterLegalStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(8.px).fontSize(14.px).lineHeight(1.6).letterSpacing(0.1.em)
            .textAlign(TextAlign.Start)
    }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("font-size", "clamp(11px, 0.97vw, 14px)")
            property("letter-spacing", "clamp(0.06em, 0.1vw, 0.1em)")
        }
    }
}

private val eventLinks = listOf(
    "Kotlin Fest 2018" to "https://kotlin.connpass.com/event/91666/",
    "Kotlin Fest 2019" to "https://kotlin.connpass.com/event/129860/",
    "Kotlin Fest 2022" to "https://2022.kotlinfest.dev/",
    "Kotlin Fest 2024" to "https://2024.kotlinfest.dev/",
    "Kotlin Fest 2025" to "https://2025.kotlinfest.dev/",
)

@Composable
private fun SocialCard(title: String, label: String, href: String) {
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 24.px).gap(12.px)
            .backgroundColor(SitePalette.brand.white).borderRadius(20.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(title, FooterSocialTitleStyle.toModifier())
        Box(Modifier.displayUntil(Breakpoint.MD)) {
            SiteButton(label, href, type = SiteButtonType.SECONDARY, language = SiteButtonLanguage.EN)
        }
        Box(Modifier.displayIfAtLeast(Breakpoint.MD)) {
            SiteButton(
                label,
                href,
                type = SiteButtonType.SECONDARY,
                size = SiteButtonSize.LARGE,
                language = SiteButtonLanguage.EN,
            )
        }
    }
}

@Composable
private fun MainLinkColumn() {
    Column(Modifier.gap(16.px)) {
        TextButton("ホーム", "/")
        TextButton("チケット", "/ticket")
        TextButton("タイムテーブル", "/timetable")
        TextButton("協賛企業一覧", SiteLinks.Sponsors)

        // 公開後にメニューへ戻す項目
        // TextButton("地図・フロア図", enabled = false)
    }
}

@Composable
private fun PolicyLinkColumn() {
    Column(Modifier.gap(16.px)) {
        TextButton("行動規範", "/code-of-conduct")
        TextButton("プライバシーポリシー", "/privacy-policy")
        TextButton("ロゴの使用について", "/brand-logo-guidelines")
        TextButton("お問い合わせ", "/contact")
    }
}

@Composable
private fun EventLinkColumn() {
    Column(Modifier.gap(16.px)) {
        eventLinks.forEach { (label, href) ->
            TextButton(label, href, language = SiteButtonLanguage.EN)
        }
    }
}

@Composable
private fun FooterLegal() {
    Column(FooterLegalStyle.toModifier()) {
        SpanText("© 2026 日本Kotlinユーザーグループ", Modifier.fontWeight(500))
        SpanText(
            "Kotlin is a registered trademark of the Kotlin Foundation, and the name Kotlin Fest is used with permission. " +
                "Kotlin Foundation is not affiliated with nor does it endorse this event.",
            Modifier.fontFamily(LatinFontFamily, "sans-serif").letterSpacing(0.12.em),
        )
    }
}

@Composable
private fun DesktopFooterBody() {
    Row(
        DesktopFooterBodyStyle.toModifier().displayIfAtLeast(Breakpoint.MD),
        verticalAlignment = Alignment.Top,
    ) {
        Image(
            "/svg/Kotlinfest2026_logo-data_1-1.svg",
            "Kotlin Fest 2026",
            DesktopFooterLogoStyle.toModifier(),
        )
        Box(DesktopFooterDividerStyle.toModifier())
        Column(DesktopFooterDetailsStyle.toModifier()) {
            Row(DesktopFooterLinksStyle.toModifier()) {
                MainLinkColumn()
                PolicyLinkColumn()
                EventLinkColumn()
            }
            FooterLegal()
        }
    }
}

@Composable
private fun MobileFooterBody() {
    Column(Modifier.fillMaxWidth().gap(48.px).displayUntil(Breakpoint.MD)) {
        Column(Modifier.fillMaxWidth().gap(16.px)) {
            Row(MobileFooterLinksStyle.toModifier()) {
                MainLinkColumn()
                PolicyLinkColumn()
            }
            EventLinkColumn()
        }
        Column(Modifier.fillMaxWidth().gap(16.px)) {
            FooterLegal()
            Row(Modifier.fillMaxWidth(), horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center) {
                Image(
                    "/svg/Kotlinfest2026_logo-data_1-1.svg",
                    "Kotlin Fest 2026",
                    Modifier.width(145.px).height(120.px),
                )
            }
        }
    }
}

@Composable
fun Footer(modifier: Modifier = Modifier) {
    Box(FooterStyle.toModifier().then(modifier), contentAlignment = Alignment.Center) {
        Column(ContentContainerStyle.toModifier().then(FooterContentStyle.toModifier())) {
            Row(FooterCtaStyle.toModifier()) {
                SocialCard("最新情報はXにて発信中！", "@kotlin_fest", SiteLinks.X)
                SocialCard("過去のイベントの様子を公開中！", "YouTube", SiteLinks.YouTube)
            }
            DesktopFooterBody()
            MobileFooterBody()
        }
    }
}
